import classNames from 'classnames';
import useCart from '@hooks/useCart';
import CartItem from '@components/CartItem';
import Link from '@components/CustomLink';
import Admonition from '@components/Admonition';
import * as format from '@format';
import Noodle from '@components/Icons/Noodle';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import { useEffect, useState } from 'react';
import { NoodleProductTypes, StripePriceUsageType } from '@typings/graphql-models';
import useLocalCurrency from '@hooks/useLocalCurrency';
import requestWithCatch from '@helpers/requestWithCatch';
import useNoodleApi from '@hooks/useNoodleApi';
import ProgressIndicator from '@components/ProgressIndicator';
import { convertCurrency, getCreator, getProrationData, getTierById } from '@tsClient';
import Gift from '@components/Icons/Gift';
import Key from '@components/Icons/Key';
import TextAlignLeft from '@components/Icons/TextAlignLeft';
import ShieldCheck from '@components/Icons/ShieldCheck';
import Info from '@components/Icons/Info';

import { logError } from '@providers/ErrorTracking';
import { useIsMobile, useUser } from '@hooks';
import getDiscountedAmount from '@helpers/getDiscountedAmount';
import hasNoodleProductType from '@helpers/hasNoodleProductType';
import { DEFAULT_CURRENCY } from '@typings/common';

import classes from './Cart.module.scss';

type Props = {
  canRemoveItems?: boolean;
  creatorSlug: string;
  showForeignCurrency?: boolean;
  onlyFreeProducts?: boolean;
  freeMembershipTitle?: string;
  isSaaSProduct?: boolean;
  isFreeMembership?: boolean;
  isFreeBooking?: boolean;
};

const convertCurrencyWrapped = requestWithCatch(convertCurrency);

const Cart: React.FC<Props> = ({
  freeMembershipTitle,
  isFreeMembership,
  isFreeBooking,
  isSaaSProduct,
  canRemoveItems = true,
  creatorSlug,
  showForeignCurrency,
  onlyFreeProducts,
}) => {
  const [user] = useUser();
  const { cart, discountCode } = useCart({ creatorSlug });
  const dueToday = cart.filter(price => !price?.trialPeriodDays).reduce((acc, val) => acc + getDiscountedAmount(val, discountCode), 0) || 0;
  const [foreignAmount, setForeignAmount] = useState<number | null>(null);
  const [foreignCurrency, setForeignCurrency] = useState<string | null>(null);
  const [prorationDue, setProrationDue] = useState<number | null>(null);
  const [name, setName] = useState<string | null | undefined>(null);
  const currency = useLocalCurrency();
  const cartCurrency = cart[0]?.currency || DEFAULT_CURRENCY;
  const fetchProration = cart[0]?.usageType === StripePriceUsageType.Recurring && cart[0].product.isTierBasedPermissioningEnabled;

  const { data: tier, getData: getTierFn, fetchingState: fetchingTier } = useNoodleApi(getTierById);
  const { getData: getProrationDataFn, fetchingState: fetchingProrationData } = useNoodleApi(getProrationData);

  useEffect(() => {
    const getName = async (): Promise<void> => {
      try {
        const data = await getCreator({ creatorSlug });
        setName(data.name);
      } catch (e) {
        logError(e);
      }
    };
    getName();
  }, [creatorSlug]);

  useEffect(() => {
    if (cart[0]?.tierId) {
      getTierFn({ id: cart[0].tierId });
    }
  }, [cart, getTierFn]);

  useEffect(() => {
    const setProrationData = async (stripePriceId: string): Promise<void> => {
      const proration = await getProrationDataFn({ stripePriceId });
      if (proration.data) {
        const amountDue = proration.data.owedAmount - proration.data.savedAmount;
        setProrationDue(amountDue > 0 ? amountDue : null);
      }
    };
    if (user && fetchProration && cart[0].stripePriceId) {
      setProrationData(cart[0].stripePriceId);
    }
  }, [cart, user, fetchProration, getProrationDataFn]);

  useEffect(() => {
    const getAmount = async (): Promise<void> => {
      if (currency && currency !== cartCurrency) {
        const { data: amount } = await convertCurrencyWrapped({ amount: dueToday, from: cartCurrency, to: currency });
        if (amount !== null) {
          setForeignAmount(amount);
          setForeignCurrency(currency);
        } else {
          setForeignAmount(null);
          setForeignCurrency(null);
        }
      }
    };
    if (dueToday && showForeignCurrency) {
      getAmount();
    }
    if (!showForeignCurrency) {
      setForeignAmount(null);
      setForeignCurrency(null);
    }
  }, [dueToday, showForeignCurrency, currency, cartCurrency]);

  const formattedDueToday = format.price.withCurrency(dueToday, cartCurrency);
  const formattedForeignPrice = foreignCurrency && foreignAmount ? format.price.withCurrency(foreignAmount, foreignCurrency) : null;
  const chargePrice = cart.find(item => (item.product?.productTypes || []).some(t => t.noodleProductType === NoodleProductTypes.Charge));
  const singlePrice = cart.length === 1 ? cart[0] : null;

  const cartHasProductsWithSchedule = cart.some(p =>
    hasNoodleProductType(p.product, NoodleProductTypes.InPersonSession, NoodleProductTypes.OnlineSession)
    && p.sessionDuration,
  );

  const cartHasRecurringScheduleProducts = cart.some(p =>
    hasNoodleProductType(p.product, NoodleProductTypes.InPersonSession, NoodleProductTypes.OnlineSession)
    && p.usageType === StripePriceUsageType.Recurring,
  );

  const hasSubscriptions = cart.find(p => p.recurringInterval);
  const showSatisfactionGuarantee = !isSaaSProduct && !onlyFreeProducts && !isFreeMembership && !isFreeBooking
    && !cartHasProductsWithSchedule && !cartHasRecurringScheduleProducts;
  const showDeliveryNotes = !chargePrice?.priceSubDescription && !isSaaSProduct && !isFreeBooking
    && !cartHasProductsWithSchedule && !cartHasRecurringScheduleProducts;

  const isMobile = useIsMobile();

  if (fetchingTier.isFetching || fetchingProrationData.isFetching) {
    return <ProgressIndicator isCentered />;
  }

  const recurringIntervals = cart
    .filter(price => price.recurringInterval)
    .map(
      price =>
        format.price.recurring({
          currency: price.currency,
          price: getDiscountedAmount(price, discountCode),
          recurringInterval: price.recurringInterval,
        }),
    )
    .filter(Boolean)
    .join(cart.filter(price => price.recurringInterval).length > 2 ? ', ' : ' and ');

  return (
    <div className={classes.cart}>
      {!isFreeMembership
        && cart.map(i => (
          <CartItem
            key={i.id}
            creatorSlug={creatorSlug}
            canRemoveItems={canRemoveItems}
            price={i}
            prorationDue={prorationDue}
            tierTitle={tier?.title}
            isSaaSProduct={isSaaSProduct}
            isOnlyItem={cart.length === 1}
          />
        ))}

      {prorationDue && cart[0] && (
        <span className={classNames('caption', classes.newSub)}>{`Prorated amount: ${format.price.withCurrency(
          cart[0].price - prorationDue,
          cartCurrency,
        )} refunded to you`}</span>
      )}

      {!!cart.length && !isFreeMembership && (
        <>
          {!isMobile && (
            <div className={classes.totalContainer}>
              <span className={classes.total}>
                Total due today
                {recurringIntervals && (
                  <>
                    <br />
                    <small>Then {recurringIntervals}</small>
                  </>
                )}
              </span>
              {onlyFreeProducts
                ? (
                  <span className={classNames(classes.totalPrice, classes.totalPriceFree)}>Free!</span>
                )
                : (
                  <span className={classes.totalPrice}>
                    {prorationDue ? format.price.withCurrency(prorationDue, cartCurrency) : formattedDueToday}
                    <br />
                    {formattedForeignPrice && <p className={classes.foreignPrice}>Approx. {formattedForeignPrice}</p>}
                  </span>
                )}
            </div>
          )}
          {!!cart.length && !isFreeMembership && isMobile && (
            <div className={classes.totalContainer}>
              <div className={classes.totalTap}>
                <span className={classes.total}>
                  Total due today
                  {recurringIntervals && (
                    <>
                      <br />
                      <small>Then {recurringIntervals}</small>
                    </>
                  )}
                </span>
              </div>
              {onlyFreeProducts
                ? (
                  <span className={classNames(classes.totalPrice, classes.totalPriceFree)}>Free!</span>
                )
                : (
                  <>
                    <span className={classes.totalPrice}>
                      {prorationDue ? format.price.withCurrency(prorationDue, cartCurrency) : formattedDueToday}
                      <br />
                      {formattedForeignPrice && <p className={classes.foreignPrice}>Approx. {formattedForeignPrice}</p>}
                    </span>
                  </>
                )}
            </div>
          )}
          {cartHasProductsWithSchedule && cartHasRecurringScheduleProducts && !isFreeBooking && (
            <span className='caption'>You will be asked to schedule your bookings each time your subscription renews. Sessions must be booked within 30 days of purchase. Unbooked sessions will be considered expired.</span>
          )}
          {cartHasProductsWithSchedule && isFreeBooking && (
            <span className="caption">Schedule your booking right here to get started!</span>
          )}
          {onlyFreeProducts && !cartHasProductsWithSchedule && (
            <>
              <Admonition
                title="Enjoy for free!"
                guidance={`This is a free piece of content from ${
                  name || 'creator'
                }. By claiming it, you will have the option to view and receive additional free content in your library - right here on their website.`}
              >
                <Gift weight="fill" color="var(--color-primary)" />
              </Admonition>
            </>
          )}
          {showSatisfactionGuarantee && (
            <>
              <Admonition
                title="Satisfaction guaranteed!"
                guidance={`${
                  hasSubscriptions ? 'You can cancel anytime. ' : ''
                }If you're not happy with your purchase, let us know and we'll make it right.`}
              >
                <ShieldCheck weight="fill" color="var(--color-primary)" />
              </Admonition>
            </>
          )}
        </>
      )}
      {!!cart.length && isFreeMembership && (
        <>
          <h2 className="heading-sm">{freeMembershipTitle}</h2>
          <Admonition
            title="Join for free!"
            guidance={`By becoming a free member, you’ll gain access to watch all of ${
              name || 'creator'
            }'s free Broadcast messages, and any free content they provide as part of their membership.`}
          >
            <ShieldCheck weight="fill" color="var(--color-primary)" />
          </Admonition>
        </>
      )}
      {cart.length === 1 && cart[0].priceDescription && cart[0].priceDescription.text !== '' && (
        <Admonition
          title="Service Description"
          guidance={
            cart[0].priceDescription.text
          }
        >
          <TextAlignLeft color="var(--color-noodle)" />
        </Admonition>
      )}
      {showDeliveryNotes && (
        <>
          <Admonition
            title="Delivery Notes"
            guidance={
              cart.find(p =>
                hasNoodleProductType(p.product, NoodleProductTypes.InPersonSession, NoodleProductTypes.OnlineSession, NoodleProductTypes.Charge),
              )
                ? (
                  <>
                    {name || 'The service provider'} will be in touch via chat, email or both to provide the service you purchased
                    {!cart.find(p => hasNoodleProductType(p.product, NoodleProductTypes.Charge))
                      ? ' at a specific location as decided by them. In many instances, the location is specified prior purchase.'
                      : '.'}{' '}
                  If you need help at any time, contact <Link to="mailto:support@noodle.shop">support@noodle.shop</Link>
                  </>
                )
                : (
                  <>
                  Digital content will become available to you directly on {name && `${name}’s`} website where you made the purchase. You’ll log in
                  with your verified email to access the content. If you need help at any time, contact{' '}
                    <Link to="mailto:support@noodle.shop">support@noodle.shop</Link>.
                  </>
                )
            }
          >
            <Key weight="fill" color="var(--color-success)" />
          </Admonition>
        </>
      )}
      {((!!chargePrice && chargePrice?.priceSubDescription) || (!!singlePrice && singlePrice?.priceSubDescription)) && (
        <>
          <Admonition title="Service Details" guidance={<div>{chargePrice?.priceSubDescription || singlePrice?.priceSubDescription}</div>}>
            <Info weight="fill" color="var(--color-primary)" />
          </Admonition>
        </>
      )}

      <Link className={classes.powered} newTab to={getUrl(IDENTIFIERS.HOME)}>
        Powered by <Noodle width={13.5} height={13.5} fill="var(--color-noodle)" /> <span style={{ color: 'var(--color-noodle)' }}>noodle</span>
      </Link>
    </div>
  );
};

export default Cart;
